import { Capacitor } from '@capacitor/core';
import posthog from 'posthog-js';
import type { App } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';
import type { UserProfile } from '@/types/api';

/** PostHog Cloud EU ingest host. Override via VITE_POSTHOG_HOST. */
const DEFAULT_API_HOST = 'https://eu.i.posthog.com';

const POSTHOG_DEFAULTS = '2026-05-30';

let initialized = false;

function resolveProjectToken(): string | null {
  const fromEnv = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
  if (typeof fromEnv === 'string' && fromEnv.trim() !== '') {
    return fromEnv.trim();
  }
  return null;
}

function resolveApiHost(): string {
  const fromEnv = import.meta.env.VITE_POSTHOG_HOST;
  if (typeof fromEnv === 'string' && fromEnv.trim() !== '') {
    return fromEnv.trim();
  }
  return DEFAULT_API_HOST;
}

export function initPostHog(app: App): void {
  const token = resolveProjectToken();
  if (!token) {
    return;
  }

  posthog.init(token, {
    api_host: resolveApiHost(),
    defaults: POSTHOG_DEFAULTS,
    person_profiles: 'identified_only',
  });

  initialized = true;

  const previousErrorHandler = app.config.errorHandler;
  app.config.errorHandler = (err, instance, info) => {
    posthog.captureException(err, { info });
    previousErrorHandler?.(err, instance, info);
  };
}

export function isPostHogEnabled(): boolean {
  return initialized;
}

export function capturePageview(to: RouteLocationNormalized): void {
  if (!initialized) {
    return;
  }

  posthog.capture('$pageview', {
    $current_url: to.fullPath,
    route_name: typeof to.name === 'string' ? to.name : undefined,
  });
}

export function syncPostHogUser(profile: UserProfile | null): void {
  if (!initialized) {
    return;
  }

  if (profile) {
    posthog.identify(profile.id, {
      email: profile.email,
      name: profile.displayName,
      auth_provider: profile.provider,
      platform: Capacitor.getPlatform(),
    });
    return;
  }

  posthog.reset();
}

export { posthog };
